import { create } from 'zustand';
import { persist, type PersistStorage } from 'zustand/middleware';

/** The selectable tabs in the Settings → Import page (`/settings/import`). */
export type ImportTab = 'Contacts' | 'Orders' | 'Resources';

export const IMPORT_TABS: readonly ImportTab[] = ['Contacts', 'Orders', 'Resources'];

interface ImportViewState {
  currentTab: ImportTab;
  setCurrentTab: (tab: ImportTab) => void;
}

const STORAGE_KEY = 'my:tms:importView';

/** Only the data is persisted — never the action. */
type PersistedImportView = Pick<ImportViewState, 'currentTab'>;

function isImportTab(value: string): value is ImportTab {
  return (IMPORT_TABS as readonly string[]).includes(value);
}

/**
 * Custom storage that persists the tab as a bare string value
 * (`my:tms:importView = Orders`), matching the convention used by the other
 * per-app caches (`my:tms:workView`, `my:tms:lng`, `my:tms:account_id`) rather
 * than wrapping it in Zustand's default `{"state":{...},"version":0}` envelope.
 *
 * Reads are validated against the known tab set — an unknown / stale value is
 * dropped so the store falls back to the `Contacts` default instead of
 * rendering an invalid tab.
 */
const storage: PersistStorage<PersistedImportView> = {
  getItem: (name) => {
    const raw = localStorage.getItem(name);
    if (!raw || !isImportTab(raw)) return null;
    return { state: { currentTab: raw } };
  },
  setItem: (name, value) => {
    localStorage.setItem(name, value.state.currentTab);
  },
  removeItem: (name) => localStorage.removeItem(name),
};

/**
 * Persists the active Import tab so a refresh / return visit lands the operator
 * back on the same tab. Every tab lives under the single `/settings/import`
 * route, so the URL can't carry this — we stash it in localStorage under
 * `my:tms:importView` (mirrors the Work View tab store).
 */
export const useImportViewStore = create<ImportViewState>()(
  persist(
    (set) => ({
      currentTab: 'Contacts',
      setCurrentTab: (tab) => set({ currentTab: tab }),
    }),
    {
      name: STORAGE_KEY,
      storage,
      partialize: (s) => ({ currentTab: s.currentTab }),
    },
  ),
);
